import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { showApiErrorToast } from '@/utils/errors';
import {
  approveInstituteSubmission,
  instituteSubmissionQueryKeys,
  rejectInstituteSubmission,
} from '../api/instituteSubmissions.api';
import { REVIEW_DECISION } from '../constants/instituteSubmission.constants';
import type { ReviewDecision, SubmissionReviewPayload } from '../types/instituteSubmission.types';

interface ReviewArgs {
  id: number;
  decision: ReviewDecision;
  payload: SubmissionReviewPayload;
}

const REVIEW_MESSAGE: Record<ReviewDecision, { success: string; failure: string }> = {
  APPROVE: {
    success: 'Submission approved',
    failure: 'Failed to approve the submission',
  },
  REJECT: {
    success: 'Submission rejected',
    failure: 'Failed to reject the submission',
  },
};

export function useReviewInstituteSubmission(onSuccess?: () => void) {
  const queryClient = useQueryClient();

  function refreshLists(id: number) {
    void queryClient.invalidateQueries({ queryKey: instituteSubmissionQueryKeys.lists() });
    void queryClient.invalidateQueries({ queryKey: instituteSubmissionQueryKeys.summaries() });
    void queryClient.invalidateQueries({ queryKey: instituteSubmissionQueryKeys.detail(id) });
  }

  return useMutation({
    mutationFn: ({ id, decision, payload }: ReviewArgs) =>
      decision === REVIEW_DECISION.APPROVE
        ? approveInstituteSubmission(id, payload)
        : rejectInstituteSubmission(id, payload),
    onSuccess: (submission, { id, decision }) => {
      queryClient.setQueryData(instituteSubmissionQueryKeys.detail(id), submission);
      refreshLists(id);
      toast.success(REVIEW_MESSAGE[decision].success);
      onSuccess?.();
    },
    onError: (error, { id, decision }) => {
      refreshLists(id);
      showApiErrorToast(error, REVIEW_MESSAGE[decision].failure);
    },
  });
}
